/**
 * MFX OS — Sales Order record page (blueprint §2.3 universal record UX).
 * Header + lineage + tabs (Summary / Lines / PO comparison / Tickets /
 * Activity) + right-rail primary action. Stage 5 line→item resolution and
 * Stage 6 ticket creation run from here as controlled commands.
 */
import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useOsDoc, useOsList, usd, fmtDate } from '../../os/core';
import { createJobTicket, markAckSent, resolveItemForOrderLine, type ResolveInput } from '../../os/commands';
import { Chip, Lineage, Modal, Timeline, Tracks } from '../../os/ui';
import type { CustomerPO, Item, SalesOrderLine, SalesOrderOS } from '../../os/types';

type Tab = 'summary' | 'lines' | 'po' | 'tickets' | 'activity';

export default function SalesOrderPage() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const id = sp.get('id') || '';
  const { data: so, loading } = useOsDoc<SalesOrderOS>('salesOrders', id);
  const { rows: lines } = useOsList<SalesOrderLine>('salesOrderLines', { wheres: [['salesOrderId', '==', id]] }, [id]);
  const { data: po } = useOsDoc<CustomerPO>('customerPOs', so?.customerPOId || null);
  const [tab, setTab] = useState<Tab>('summary');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [resolving, setResolving] = useState<(SalesOrderLine & { id: string }) | null>(null);

  const sorted = useMemo(() => [...lines].sort((a, b) => a.lineNo - b.lineNo), [lines]);
  const unresolved = sorted.filter((l) => l.resolution === 'unresolved').length;

  const run = async (fn: () => Promise<unknown>, after?: (r: unknown) => void) => {
    setBusy(true); setErr('');
    try { const r = await fn(); after?.(r); } catch (e) { setErr((e as Error).message); }
    setBusy(false);
  };

  if (loading) return <div className="loading"><div className="spinner" /> Loading sales order…</div>;
  if (!so) return <div className="empty">Sales order not found.</div>;

  const primaryAction = !so.jobTicketIds?.length
    ? (unresolved
      ? { label: `Resolve ${unresolved} line(s) to items`, onClick: () => setTab('lines') }
      : { label: '▸ Create Ticket + Production Jobs', onClick: () => run(() => createJobTicket(so, sorted), (r) => nav(`/os/ticket?id=${r}`)) })
    : { label: `Open ${so.jobTicketIds.length > 1 ? 'tickets' : 'ticket'}`, onClick: () => nav(`/os/ticket?id=${so.jobTicketIds[0]}`) };

  return (
    <>
      <Lineage parts={[
        { label: so.customerName || 'Client' },
        ...(so.quoteNum ? [{ label: `Quote ${so.quoteNum}` }] : []),
        ...(po ? [{ label: `PO ${po.poNumber}`, to: `/os/po?id=${po.id}` }] : [{ label: `PO ${so.poNumber}` }]),
        { label: so.soNum },
        ...(so.jobTicketIds || []).map((t) => ({ label: t.toUpperCase(), to: `/os/ticket?id=${t}` })),
      ]} />

      <div className="page-head">
        <div>
          <div className="page-title">{so.soNum} <Chip label={so.tracks?.commercial || so.status} /></div>
          <div className="page-sub">{so.customerName} · PO {so.poNumber} · {so.skuCount || sorted.length} line(s) · requested {fmtDate(so.requestedDate)} · committed {so.promisedDate ? fmtDate(so.promisedDate) : 'pending'}</div>
          <div style={{ marginTop: 8 }}><Tracks tracks={so.tracks} /></div>
        </div>
        <div className="os-rail">
          <button className="btn primary" disabled={busy} onClick={primaryAction.onClick}>{primaryAction.label}</button>
          {so.ack && !so.ack.sentAt && <button className="btn" disabled={busy} onClick={() => run(() => markAckSent(so))}>✉ Mark acknowledgment sent</button>}
        </div>
      </div>

      {err && <div className="os-err">{err}</div>}

      <div className="seg" style={{ marginBottom: 14 }}>
        {(['summary', 'lines', 'po', 'tickets', 'activity'] as Tab[]).map((t) => (
          <button key={t} className={tab === t ? 'on' : ''} onClick={() => setTab(t)}>
            {t === 'po' ? 'PO comparison' : t === 'lines' ? `Lines${unresolved ? ` (${unresolved} unresolved)` : ''}` : t[0].toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {tab === 'summary' && (
        <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 14, alignItems: 'start' }}>
          <div className="card os-card">
            <div className="os-sect" style={{ marginTop: 0 }}>Commercial summary</div>
            <div className="os-kv-grid">
              <div><em>Client</em><span>{so.customerName}</span></div>
              <div><em>Client email</em><span>{so.email || '—'}</span></div>
              <div><em>Customer PO</em><span>{so.poNumber}</span></div>
              <div><em>Quote locked</em><span>{so.quoteLock ? `${so.quoteLock.quoteNum} @ ${fmtDate(so.quoteLock.lockedAt)}` : '—'}</span></div>
              <div><em>PO locked</em><span>{so.poLock ? `v${so.poLock.poVersion} @ ${fmtDate(so.poLock.lockedAt)}` : '—'}</span></div>
              <div><em>Order total</em><span style={{ color: 'var(--gn)', fontWeight: 700 }}>{usd(so.total)}</span></div>
              <div><em>Requested</em><span>{fmtDate(so.requestedDate)}</span></div>
              <div><em>Committed</em><span>{so.promisedDate ? fmtDate(so.promisedDate) : 'pending'}</span></div>
            </div>
          </div>
          <div className="card os-card">
            <div className="os-sect" style={{ marginTop: 0 }}>
              Order acknowledgment {so.ack?.sentAt ? <Chip label="sent" /> : <Chip label="draft" />}
              {so.ack?.sentAt && <span style={{ color: 'var(--tx3)', fontSize: 11 }}> · {fmtDate(so.ack.sentAt)} by {so.ack.sentBy}</span>}
            </div>
            {so.ack ? (
              <>
                <pre className="os-raw" style={{ maxHeight: 320 }}>{so.ack.body}</pre>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn sm" onClick={() => { navigator.clipboard?.writeText(so.ack!.body); }}>⧉ Copy text</button>
                  {!so.ack.sentAt && <button className="btn sm primary" disabled={busy} onClick={() => run(() => markAckSent(so))}>✉ Mark sent</button>}
                </div>
                <div className="page-sub" style={{ marginTop: 8 }}>Customer-safe: no margins, costs, internal notes or approval discussions. Send it via your email — automatic client email stays OFF by policy.</div>
              </>
            ) : <div className="empty">No acknowledgment draft.</div>}
          </div>
        </div>
      )}

      {tab === 'lines' && (
        <div className="card os-card">
          <div className="table-wrap"><table>
            <thead><tr><th>#</th><th>Customer part</th><th>Description</th><th>Qty</th><th>Unit (quote)</th><th>Unit (PO)</th><th>Total</th><th>Item resolution</th><th></th></tr></thead>
            <tbody>
              {sorted.map((l) => (
                <tr key={l.id}>
                  <td>{l.lineNo}</td>
                  <td>{l.customerPart || '—'}</td>
                  <td>{l.description}</td>
                  <td>{l.qty.toLocaleString()}</td>
                  <td>${l.unitPrice.toFixed(4)}</td>
                  <td style={{ color: l.poUnitPrice !== undefined && Math.abs((l.poUnitPrice || 0) - l.unitPrice) > 0.0005 ? 'var(--or)' : 'var(--tx2)' }}>{l.poUnitPrice !== undefined ? `$${l.poUnitPrice.toFixed(4)}` : '—'}</td>
                  <td>{usd(l.total)}</td>
                  <td>
                    {l.resolution === 'unresolved'
                      ? <Chip label="unresolved" tone="var(--rd)" />
                      : <span><Chip label={l.resolution} /> <span style={{ color: 'var(--tx2)', fontSize: 12 }}>{l.internalPart}{l.blueprintRevCode ? ` · Rev ${l.blueprintRevCode}` : ''}</span></span>}
                  </td>
                  <td>{l.resolution === 'unresolved' && !so.jobTicketIds?.length && <button className="btn sm primary" onClick={() => setResolving(l)}>Resolve…</button>}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
          {!so.jobTicketIds?.length && !unresolved && sorted.length > 0 && (
            <div className="os-clear" style={{ marginTop: 10 }}>✓ Every line resolved — you can create the ticket + production jobs.</div>
          )}
        </div>
      )}

      {tab === 'po' && (
        <div className="card os-card">
          {po?.comparison ? (
            <div className="table-wrap"><table>
              <thead><tr><th>Field</th><th>Quote</th><th>PO</th><th>Result</th></tr></thead>
              <tbody>{po.comparison.rows.map((r, i) => (
                <tr key={i}><td style={{ color: 'var(--tx2)' }}>{r.label}</td><td>{r.quote}</td><td>{r.po}</td><td>{r.verdict}</td></tr>
              ))}</tbody>
            </table></div>
          ) : <div className="empty">Comparison snapshot lives on the PO record.</div>}
          {po && <button className="btn sm" style={{ marginTop: 10 }} onClick={() => nav(`/os/po?id=${po.id}`)}>Open {po.displayId}</button>}
        </div>
      )}

      {tab === 'tickets' && (
        <div className="card os-card">
          {(so.jobTicketIds || []).map((t) => (
            <button key={t} className="os-pick" onClick={() => nav(`/os/ticket?id=${t}`)}><strong>{t.toUpperCase()}</strong> — open ticket</button>
          ))}
          {!so.jobTicketIds?.length && (
            <div className="empty">
              No ticket yet. {unresolved ? `${unresolved} line(s) must be resolved to items first.` : 'All lines resolved — create it from the header action.'}
            </div>
          )}
        </div>
      )}

      {tab === 'activity' && <div className="card os-card"><Timeline entityId={so.id} /></div>}

      <ResolveModal line={resolving} onClose={() => setResolving(null)} busy={busy}
        onSubmit={(input) => resolving && run(() => resolveItemForOrderLine(resolving, input), () => setResolving(null))} />
    </>
  );
}

// ── Stage 5 wizard: existing / changed / new ─────────────────────────────
function ResolveModal({ line, onClose, onSubmit, busy }: { line: (SalesOrderLine & { id: string }) | null; onClose: () => void; onSubmit: (r: ResolveInput) => void; busy: boolean }) {
  const [mode, setMode] = useState<'existing' | 'changed' | 'new'>('existing');
  const { rows: items } = useOsList<Item>('items', { limit: 300 });
  const [pick, setPick] = useState('');
  const [f, setF] = useState({ name: '', customerPart: '', family: '', changeNote: '', revReason: '' });
  const [filter, setFilter] = useState('');

  if (!line) return null;
  const shown = items.filter((x) => !filter.trim() || `${x.internalPart} ${x.customerPart} ${x.name}`.toLowerCase().includes(filter.toLowerCase())).slice(0, 10);
  const canGo = mode === 'existing' ? !!pick : mode === 'new' ? !!(f.name || line.description) : !!(pick && f.changeNote && f.revReason);

  const submit = () => {
    if (mode === 'existing') onSubmit({ mode, itemId: pick });
    else if (mode === 'new') onSubmit({ mode, name: f.name || line.description, customerPart: f.customerPart || line.customerPart, family: f.family || undefined, revReason: f.revReason || undefined });
    else onSubmit({ mode, itemId: pick, changeNote: f.changeNote, revReason: f.revReason });
  };

  return (
    <Modal open title={`Resolve line ${line.lineNo}: ${line.description || line.customerPart}`} onClose={onClose}>
      <div className="seg" style={{ marginBottom: 12 }}>
        <button className={mode === 'existing' ? 'on' : ''} onClick={() => setMode('existing')}>Existing item</button>
        <button className={mode === 'changed' ? 'on' : ''} onClick={() => setMode('changed')}>Changed item</button>
        <button className={mode === 'new' ? 'on' : ''} onClick={() => setMode('new')}>New item</button>
      </div>

      {(mode === 'existing' || mode === 'changed') && (
        <>
          <input placeholder="Search item registry…" value={filter} onChange={(e) => setFilter(e.target.value)} />
          <div className="os-quote-picks">
            {shown.map((x) => (
              <button key={x.id} className={`os-pick${pick === x.id ? ' on' : ''}`} onClick={() => setPick(pick === x.id ? '' : x.id)}>
                <strong>{x.internalPart}</strong> {x.name} <span style={{ color: 'var(--tx3)' }}>{x.customerPart || ''} · Rev {x.currentRevCode || '—'}</span>
              </button>
            ))}
            {!shown.length && <div className="empty" style={{ padding: 8 }}>No items match — onboard it as a new item.</div>}
          </div>
        </>
      )}

      {mode === 'new' && (
        <div className="os-form-grid">
          <label>Item name<input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder={line.description} /></label>
          <label>Customer part<input value={f.customerPart} onChange={(e) => setF({ ...f, customerPart: e.target.value })} placeholder={line.customerPart} /></label>
          <label>Product family<input value={f.family} onChange={(e) => setF({ ...f, family: e.target.value })} placeholder="pouch / label / film" /></label>
          <label>Rev A note<input value={f.revReason} onChange={(e) => setF({ ...f, revReason: e.target.value })} placeholder="Initial onboarding" /></label>
        </div>
      )}

      {mode === 'changed' && (
        <div className="os-form-grid" style={{ marginTop: 10 }}>
          <label className="os-span2">What changed? *<input value={f.changeNote} onChange={(e) => setF({ ...f, changeNote: e.target.value })} placeholder="New artwork / dimensions / material…" /></label>
          <label className="os-span2">Revision reason *<input value={f.revReason} onChange={(e) => setF({ ...f, revReason: e.target.value })} placeholder="Why a new blueprint revision is required" /></label>
        </div>
      )}

      <div className="page-sub" style={{ marginTop: 10 }}>
        {mode === 'existing' && 'Reuses the item and its approved blueprint revision — prepress gate pre-clears.'}
        {mode === 'changed' && 'Creates a DRAFT blueprint revision; prepress must approve it before the job clears.'}
        {mode === 'new' && 'Onboards the item with a DRAFT Rev A blueprint; prepress completes and approves it.'}
      </div>

      <div className="os-modal-actions">
        <button className="btn" onClick={onClose}>Cancel</button>
        <button className="btn primary" disabled={!canGo || busy} onClick={submit}>{busy ? 'Working…' : 'Resolve line'}</button>
      </div>
    </Modal>
  );
}
